import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Drawer,
  useMediaQuery,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Container,
} from "@mui/material";

import SesionsService from "../../services/SesionsService";
import DescargasService from "../../services/DownloadService";
import buscadorIcon from '../../assets/buscador.png';
import { canGenerate } from "../../core/sesionGenerator/sesion.logic";
import { useSesionGeneration } from "../../core/sesionGenerator/hooks/useSesionGeneration";
import ProgressBar from "../../core/sesionGenerator/components/ProgressBar";
import { useAuth } from "../../context/AuthContext";
import { useSesionSearch } from "./hooks/useSesionSearch";
import FiltrosPanel from "./components/FiltrosPanel";
import { useSesionSelection } from "./hooks/useSesionSelection";
import SesionTable from "./components/SesionTable";
import SesionEnModal from "../modal_pdf";
import "./sesion-search.css";
import HeroHeader from "../HeroHeader";

const LOCALSTORAGE_KEYS_TO_CLEAR = [
  "sac_hash_step3",
  "sac_step2_data",
  "sac_step5_data",
  "sac_temas_generados",
  "seleccionPaso1",
  "seleccionPaso2",
  "seleccionPaso2_key",
  "seleccionPaso3",
  "sesionesPaso3",
  "sesionesPaso3_key",
  "temasPaso3",
  "temasPaso3_key",
  "ultimoMongoId",
];

const SesionSearchPage = () => {
  const SEARCH_RESULTS_SCROLL_HEIGHT = "calc(100vh - 220px)";
  const SEARCH_RESULTS_PANEL_HEIGHT = "calc(100vh - 140px)";
  const { user } = useAuth();
  const role = user?.rol?.nombre;
  const userUbigeo = user?.ubicacion?.ubigeo || "";
  const ieUsuarioId = user?.institucion?.id || "";
  const nombreIeUsuario = user?.institucion?.nombre || "";

  useEffect(() => {
    LOCALSTORAGE_KEYS_TO_CLEAR.forEach((key) => {
      localStorage.removeItem(key);
    });
  }, []);

  const [downloading, setDownloading] = useState({
    word: false,
    pdf: false,
    html: false,
  });

  const isDownloading = downloading.word || downloading.pdf || downloading.html;

  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const [sesionParaGenerar, setSesionParaGenerar] = useState(null);

  const stepLabels = {
    mongo: "Sesión",
    word: "Documento Word",
    pdf: "Documento PDF",
    html: "Vista HTML",
  };

  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [descargaData, setDescargaData] = useState({
    mongo_id: null,
    tipo_sesion: null,
    sesion: null,
  });

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [sesionAEliminar, setSesionAEliminar] = useState(null);
  const [initialSearchHydrated, setInitialSearchHydrated] = useState(false);
  const [autoDistrictSearchResolved, setAutoDistrictSearchResolved] = useState(false);

  const isMobile = useMediaQuery("(max-width:1500px)");
  const [modalOpen, setModalOpen] = useState(false);
  const [sesionSeleccionada, setSesionSeleccionada] = useState(null);

  const {
    search, setSearch,
    appliedSearch,
    usuario, setUsuario,
    fechaDesde, setFechaDesde,
    fechaHasta, setFechaHasta,
    nivelId, setNivelId,
    gradoId, setGradoId,
    areaId, setAreaId,

    tipoSesion, setTipoSesion,
    estado, setEstado,
    regionId, setRegionId,
    provinciaId, setProvinciaId,
    distritoId, setDistritoId,
    ieId, setIeId,
    instituciones,
    regiones,
    provincias,
    distritos,

    vistaDistrital,
    setVistaDistrital,

    usuarios,
    niveles,
    grados,
    areas,
    filtered,
    total,
    loading,
    page, setPage,
    rowsPerPage, setRowsPerPage,
    handleSearch,
    showAuthorFilter,
  } = useSesionSearch((role || "").toUpperCase(), userUbigeo, ieUsuarioId);

  const {
    modalProgress,
    currentMongoStep,
    generacionActiva,
    generateError,
    invalidPdfIds,
    startGenerate,
    addInvalidPdf,
  } = useSesionGeneration({
    onFinish: () => handleSearch(page, rowsPerPage),
  });

  const {
    selectedIds,
    setSelectedIds,
    toggleRow,
    clearSelection,
  } = useSesionSelection();

  const STEP_DESCRIPTIONS = {
    inicio: "Iniciando generación de la sesión...",
    competencias: "Cargando competencias y desempeños...",
    prompt_1: "Definiendo desempeños de aprendizaje...",
    videos: "Buscando videos educativos...",
    prompt_2: "Preparando la estructura de la sesión...",
    prompt_3: "Generando la secuencia pedagógica...",
    prompt_4: "Desarrollando la actividad central...",
    prompt_5: "Construyendo el cierre...",
    prompt_6: "Definiendo acciones previas...",
    prompt_7: "Generando lista de cotejo...",
    prompt_8: "Definiendo criterios de evaluación...",
    prompt_9: "Creando ficha de trabajo...",
    prompt_10: "Generando banco de preguntas...",
    final: "Finalizando generación...",
  };

  const [openDrawer, setOpenDrawer] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");
  const [openError, setOpenError] = useState(false);

  const handleFilterSearch = (
    overrideFilters = {},
    options = { closeDrawer: true }
  ) => {
    setPage(0);
    handleSearch(0, rowsPerPage, overrideFilters);

    if (isMobile && options.closeDrawer) {
      setOpenDrawer(false);
    }
  };

  useEffect(() => {
    const loadInitialSessions = async () => {
      await handleSearch(0, rowsPerPage, {});
      setInitialSearchHydrated(true);
    };

    loadInitialSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialSearchHydrated || autoDistrictSearchResolved) return;
    if (!role) return;

    const canUseDistrictSearchByDefault = role && role !== "ADMIN";
    const hasManualFilters = Boolean(
      search ||
      usuario ||
      fechaDesde ||
      fechaHasta ||
      nivelId ||
      gradoId ||
      areaId ||
      tipoSesion ||
      estado ||
      regionId ||
      provinciaId ||
      distritoId ||
      ieId
    );

    if (!canUseDistrictSearchByDefault || hasManualFilters || vistaDistrital || total > 0) {
      setAutoDistrictSearchResolved(true);
      return;
    }

    if (total === 0) {
      setAutoDistrictSearchResolved(true);
      setVistaDistrital(true);
      setPage(0);
      handleSearch(0, rowsPerPage, {
        vista_distrital: true,
        id_institucion_edu: "",
      });
    }
  }, [
    initialSearchHydrated,
    autoDistrictSearchResolved,
    role,
    search,
    usuario,
    fechaDesde,
    fechaHasta,
    nivelId,
    gradoId,
    areaId,
    tipoSesion,
    estado,
    regionId,
    provinciaId,
    distritoId,
    ieId,
    vistaDistrital,
    total,
    rowsPerPage,
    handleSearch,
    setVistaDistrital,
    setPage,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (generacionActiva) {
        e.preventDefault();
        e.returnValue = "Se está generando una sesión. Si sales ahora, perderás el progreso. ¿Estás seguro?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [generacionActiva]);

  const handleOpenModal = (sesion) => {
    setSesionSeleccionada(sesion);
    setModalOpen(true);
  };

  const handleDownload = (sesion) => {
    setDescargaData({
      mongo_id: sesion.mongo_id,
      tipo_sesion: sesion.tipo_sesion,
      sesion,
    });
    setOpenDownloadModal(true);
  };

  const confirmarEliminar = async () => {
    if (!sesionAEliminar) return;

    try {
      if (sesionAEliminar.multiple) {
        await Promise.all(
          sesionAEliminar.ids.map((id) =>
            SesionsService.eliminarSesion(id)
          )
        );
        clearSelection();
      } else {
        await SesionsService.eliminarSesion(sesionAEliminar.id);
      }

      setOpenConfirmDelete(false);
      setSesionAEliminar(null);

      handleSearch(page, rowsPerPage);
    } catch (error) {
      console.error("Error al eliminar sesiones", error);
      setErrorMsg("No se pudieron eliminar las sesiones seleccionadas.");
      setOpenError(true);
    }
  };

  const descargarArchivo = async (tipoArchivo) => {
    const { mongo_id, tipo_sesion, sesion } = descargaData;

    if (!mongo_id || !tipo_sesion) {
      setErrorMsg("No se encontró información suficiente para descargar la sesión.");
      setOpenError(true);
      return;
    }

    try {
      setDownloading((prev) => ({ ...prev, [tipoArchivo]: true }));

      await DescargasService.descargarArchivoSesion(mongo_id, tipo_sesion, tipoArchivo, sesion);

      setOpenDownloadModal(false);
    } catch (error) {
      console.error(`Error al descargar ${tipoArchivo}:`, error);
      // Manejo de errores
    } finally {
      setDownloading((prev) => ({ ...prev, [tipoArchivo]: false }));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;

    setSesionAEliminar({
      multiple: true,
      ids: selectedIds,
    });

    setOpenConfirmDelete(true);
  };

  const handleOpenGenerate = (sesion) => {
    if (!canGenerate(sesion, invalidPdfIds)) return;

    setSesionParaGenerar(sesion);
    setOpenGenerateModal(true);

    setTimeout(() => startGenerate(sesion), 0);
  };


  const handleCloseGenerateModal = async () => {
    if (generacionActiva) return;

    setOpenGenerateModal(false);
    setSesionParaGenerar(null);

    await handleSearch(page, rowsPerPage);
  };

  const handleClearFilters = () => {
    setSearch("");
    setUsuario("");
    setNivelId("");
    setGradoId("");
    setAreaId("");
    setFechaDesde("");
    setFechaHasta("");
    setTipoSesion("");
    setEstado("");
    setRegionId("");
    setProvinciaId("");
    setDistritoId("");
    setIeId("");
    setVistaDistrital(false);
    setPage(0);

    handleSearch(0, rowsPerPage, {
      q: "",
      id_usuario: "",
      nivel_id: "",
      grado_id: "",
      area_tematica_id: "",
      fecha_inicio: "",
      fecha_fin: "",
      tipo_sesion: "",
      estado: "",
      region: "",
      provincia: "",
      distrito: "",
      id_institucion_edu: "",
      vista_distrital: false,
    });
  };

  const selectableVisibleIds = filtered
    .filter((s) => s.puede_eliminar)
    .map((s) => s.id);

  const allSelected =
    selectableVisibleIds.length > 0 &&
    selectableVisibleIds.every((id) => selectedIds.includes(id));

  const someSelected =
    selectableVisibleIds.some((id) => selectedIds.includes(id));

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !selectableVisibleIds.includes(id))
      );
    } else {
      setSelectedIds((prev) => [
        ...new Set([...prev, ...selectableVisibleIds]),
      ]);
    }
  };

  const canSelect = role === "ADMIN" || role === "PROFESOR" || role === "DIRECTOR";
  const canDelete = role === "ADMIN" || role === "PROFESOR" || role === "DIRECTOR";

  const canGenerateSesionByRole =
    role === "ADMIN" || role === "DIRECTOR" || role === "PROFESOR"
      ? (sesion) => canGenerate(sesion, invalidPdfIds)
      : () => false;
  return (
    <Box
      className="sesion-page"
      sx={{
        bgcolor: "#f5f7fa",
        display: "flex",
        flexDirection: "column",
        p: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <HeroHeader
        img={buscadorIcon}
        subtitle="PLANIFICADOR DE SESIONES"
        title="Buscador de Sesiones"
        description="Explora y localiza rápidamente sesiones de aprendizaje alineadas al currículo, adaptadas a distintos niveles y áreas temáticas, para facilitar su planificación y ejecución."
        maxWidth="xxl"
      />
      <Container maxWidth="xxl" sx={{ mt: 2 }}>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {!isMobile && (
            <Grid item md={2}>
              <Paper
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
                  width: "100%",
                  maxHeight: SEARCH_RESULTS_PANEL_HEIGHT,
                  height: SEARCH_RESULTS_PANEL_HEIGHT,
                  display: "flex",
                }}
              >
                <Box sx={{ width: "100%", height: "100%" }}>
                  <FiltrosPanel
                    isMobile={false}
                    panelHeight={SEARCH_RESULTS_PANEL_HEIGHT}
                    maxPanelHeight={SEARCH_RESULTS_PANEL_HEIGHT}
                    search={search} setSearch={setSearch}
                    usuario={usuario} setUsuario={setUsuario}
                    userRole={role}
                    fechaDesde={fechaDesde} setFechaDesde={setFechaDesde}
                    fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
                    nivelId={nivelId} setNivelId={setNivelId}
                    gradoId={gradoId} setGradoId={setGradoId}
                    areaId={areaId} setAreaId={setAreaId}

                    tipoSesion={tipoSesion} setTipoSesion={setTipoSesion}
                    estado={estado} setEstado={setEstado}
                    regionId={regionId} setRegionId={setRegionId}
                    provinciaId={provinciaId} setProvinciaId={setProvinciaId}
                    distritoId={distritoId} setDistritoId={setDistritoId}
                    ieId={ieId}
                    setIeId={setIeId}
                    instituciones={instituciones}

                    usuarios={usuarios}
                    showAuthorFilter={showAuthorFilter}
                    niveles={niveles}
                    grados={grados}
                    areas={areas}
                    regiones={regiones}
                    provincias={provincias}
                    distritos={distritos}

                    vistaDistrital={vistaDistrital}
                    setVistaDistrital={setVistaDistrital}

                    ieUsuarioId={ieUsuarioId}
                    nombreIeUsuario={nombreIeUsuario}

                    onBuscar={handleFilterSearch}
                    onLimpiar={handleClearFilters}
                  />
                </Box>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={isMobile ? 12 : 10} sx={{ maxWidth: "1100px", width: "100%" }}>
            <SesionTable
              data={filtered}
              loading={loading}
              total={total}
              page={page}
              rowsPerPage={rowsPerPage}
              search={appliedSearch}
              tableHeight={SEARCH_RESULTS_PANEL_HEIGHT}
              maxTableHeight={SEARCH_RESULTS_SCROLL_HEIGHT}
              isMobile={isMobile}
              onOpenFilters={() => setOpenDrawer(true)}
              canSelect={canSelect}
              canDelete={canDelete}
              canGenerateSesion={canGenerateSesionByRole}
              selectedIds={selectedIds}
              allSelected={allSelected}
              someSelected={someSelected}
              onToggleRow={toggleRow}
              onToggleAll={handleToggleAll}
              onDeleteSelected={handleDeleteSelected}
              canSelectRow={(sesion) => Boolean(sesion?.puede_eliminar)}
              generacionActiva={generacionActiva}
              onGenerate={handleOpenGenerate}
              onView={handleOpenModal}
              onDownload={handleDownload}
              onClearFilters={handleClearFilters}
              onPageChange={(newPage) => {
                setPage(newPage);
                handleSearch(newPage, rowsPerPage);
              }}
              onRowsPerPageChange={(newRows) => {
                setRowsPerPage(newRows);
                setPage(0);
                handleSearch(0, newRows);
              }}
            />
          </Grid>
        </Grid>
      </Container>

      <Drawer
        anchor="left"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          className: "drawer-redondeado" 
        }}
      >
        <Box
          sx={{
            height: "100%",
            px: { xs: 2.5, sm: 3 },
            py: { xs: 3, sm: 4 },
          }}
        >
          <Box sx={{ maxHeight: "100%", overflowY: "auto" }}>
            <FiltrosPanel
              isMobile
              onCloseMobile={() => setOpenDrawer(false)}

              search={search} setSearch={setSearch}
              usuario={usuario} setUsuario={setUsuario}
              fechaDesde={fechaDesde} setFechaDesde={setFechaDesde}
              fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
              nivelId={nivelId} setNivelId={setNivelId}
              gradoId={gradoId} setGradoId={setGradoId}
              areaId={areaId} setAreaId={setAreaId}

              tipoSesion={tipoSesion} setTipoSesion={setTipoSesion}
              estado={estado} setEstado={setEstado}
              regionId={regionId} setRegionId={setRegionId}
              provinciaId={provinciaId} setProvinciaId={setProvinciaId}
              distritoId={distritoId} setDistritoId={setDistritoId}

              usuarios={usuarios}
              showAuthorFilter={showAuthorFilter}
              niveles={niveles}
              grados={grados}
              areas={areas}
              regiones={regiones}
              provincias={provincias}
              distritos={distritos}
              ieId={ieId}
              setIeId={setIeId}
              instituciones={instituciones}

              vistaDistrital={vistaDistrital}
              setVistaDistrital={setVistaDistrital}

              ieUsuarioId={ieUsuarioId}
              nombreIeUsuario={nombreIeUsuario}

              onBuscar={handleFilterSearch}
              onLimpiar={handleClearFilters}
              userRole={role}
            />
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={openError}
        autoHideDuration={4000}
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setOpenError(false)}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openConfirmDelete} onClose={() => setOpenConfirmDelete(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {sesionAEliminar?.multiple
              ? `¿Está seguro de que desea eliminar ${sesionAEliminar.ids.length} sesiones seleccionadas? Esta acción no se puede deshacer.`
              : `¿Está seguro de que desea eliminar la sesión de aprendizaje "${sesionAEliminar?.tema}"? Esta acción no se puede deshacer.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDelete(false)} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmarEliminar} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDownloadModal}
        onClose={(event, reason) => {
          if (isDownloading) return; 
          setOpenDownloadModal(false);
        }}
        disableEscapeKeyDown={isDownloading}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Descargar sesión</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Seleccione el formato de descarga
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              variant="contained"
              disabled={isDownloading}
              onClick={() => descargarArchivo("word")}
              startIcon={downloading.word ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {downloading.word ? "Descargando Word..." : "Descargar Word"}
            </Button>
            <Button
              variant="contained"
              disabled={isDownloading}
              onClick={() => descargarArchivo("pdf")}
              startIcon={downloading.pdf ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {downloading.pdf ? "Descargando PDF..." : "Descargar PDF"}
            </Button>
            <Button
              variant="contained"
              disabled={isDownloading}
              onClick={() => descargarArchivo("html")}
              startIcon={downloading.html ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {downloading.html ? "Descargando HTML..." : "Descargar HTML"}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDownloadModal(false)} disabled={isDownloading}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openGenerateModal}
        onClose={(event, reason) => {
          if (generacionActiva) return;
          handleCloseGenerateModal();
        }}
        disableEscapeKeyDown={generacionActiva}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography component="div" variant="subtitle2" color="text.secondary">
            Proceso de generación
          </Typography>
          <Typography component="div" variant="h6" sx={{ fontWeight: 600 }}>
            {sesionParaGenerar?.tema || "Tema sin nombre"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {generateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {generateError}
            </Alert>
          )}
          {modalProgress.mongo.estado === "en_progreso" && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {STEP_DESCRIPTIONS[currentMongoStep] ?? "Procesando información…"}
            </Typography>
          )}
          {["mongo", "word", "pdf", "html"].map((s) => {
            const stepData = modalProgress[s] ?? {};
            return (
              <ProgressBar
                key={s}
                label={stepLabels[s]}
                estado={stepData.estado}
                progreso={stepData.progreso ?? 0}
                tiempo={stepData.tiempo ?? 0}
              />
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseGenerateModal}
            disabled={generacionActiva}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <SesionEnModal
        modalOpen={modalOpen}
        sesionSeleccionada={sesionSeleccionada}
        handleCloseModal={() => setModalOpen(false)}
        onPdfNotFound={addInvalidPdf}
      />
    </Box>
  );
};

export default SesionSearchPage;
